import { useEffect, useRef } from "react";

const APPLIED_LISTENERS = new Set<number>();
let AUTO_INCREMENTED_ID = 0;

/**
 * bind the given listener to clicks happening outside any of the reference
 * nodes (useful when binding automatic closing of toggled layers and similar).
 */
export function useClickOutsideListener(
  listener: ((evt: MouseEvent) => void) | undefined,
  ...refs: React.RefObject<HTMLElement | null>[]
): void {
  const listenerId = useRef(++AUTO_INCREMENTED_ID).current;

  useEffect(() => {
    if (listener === undefined) {
      return;
    }

    APPLIED_LISTENERS.add(listenerId);

    const handleClick = (evt: MouseEvent): void => {
      const targetNode = evt.target as Node | null;

      if (targetNode === null) {
        return;
      }

      if (listenerId !== getLastAddedItem(APPLIED_LISTENERS)) {
        // only the last applied clickOutside listener may be triggered in a
        // single click; otherwise we could accidentally close multiple layers
        // of ui at once even if that's not what the user intended to do
        return;
      }

      // check if the clicked node is contained within one of the given refs, if
      // no matching parent node was given then bail out
      const containedWithinRef = refs.find((ref) => {
        return (
          ref.current !== null &&
          (ref.current === targetNode || ref.current.contains(targetNode))
        );
      });

      if (containedWithinRef === undefined) {
        listener(evt);
      }
    };

    const applyListenerTimeout = window.setTimeout(() =>
      window.addEventListener("click", handleClick)
    );

    return (): void => {
      window.clearTimeout(applyListenerTimeout);
      window.removeEventListener("click", handleClick);

      APPLIED_LISTENERS.delete(listenerId);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listener, listenerId, ...refs]);
}

function getLastAddedItem<T>(set: Set<T>): T | undefined {
  const values = set.values();

  let prevValue: IteratorResult<T, undefined> | undefined;
  let currValue: IteratorResult<T, undefined> | undefined;

  do {
    prevValue = currValue;
    currValue = values.next();
  } while (currValue.done !== true);

  if (prevValue !== undefined) {
    return prevValue.value;
  }
}
